import type { SingleTargetKind } from './calculatorSession';

/**
 * Ordinary positive decimal text for calculator gating.
 * Rejects scientific notation, hex, and other non-decimal forms that
 * `Number()` would otherwise accept.
 */
const STRICT_POSITIVE_DECIMAL = /^(?:\d+(?:\.\d*)?|\.\d+)$/;

export type StrictDecimalParse =
  | { ok: true; value: number }
  | {
      ok: false;
      reason: 'empty' | 'malformed' | 'non_positive' | 'non_finite';
    };

export function parseStrictPositiveDecimal(raw: string): StrictDecimalParse {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, reason: 'empty' };
  if (!STRICT_POSITIVE_DECIMAL.test(trimmed)) {
    return { ok: false, reason: 'malformed' };
  }
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return { ok: false, reason: 'non_finite' };
  if (!(value > 0)) return { ok: false, reason: 'non_positive' };
  return { ok: true, value };
}

/** Potency usable for single-strain Calculate (finite, > 0, ≤ 100). */
export function hasUsableSingleStrainThc(
  thcPercent: number | null | undefined,
): thcPercent is number {
  return (
    typeof thcPercent === 'number' &&
    Number.isFinite(thcPercent) &&
    thcPercent > 0 &&
    thcPercent <= 100
  );
}

export type SingleStrainValidationInput = {
  kind: SingleTargetKind;
  weightText: string;
  targetText: string;
  thcPercent: number | null | undefined;
  hasStrain: boolean;
};

/**
 * Mode-specific Calculate disable reason, or null when Calculate may run.
 */
export function getSingleStrainValidationHint(
  input: SingleStrainValidationInput,
): string | null {
  if (!input.hasStrain) return 'Select a strain first.';
  if (!hasUsableSingleStrainThc(input.thcPercent)) {
    return 'This strain needs a valid THC percentage greater than zero.';
  }

  if (input.kind === 'target_weight') {
    const parsed = parseStrictPositiveDecimal(input.weightText);
    if (!parsed.ok) {
      if (parsed.reason === 'empty') {
        return 'Enter a joint weight in grams.';
      }
      if (parsed.reason === 'malformed') {
        return 'Joint weight must be an ordinary decimal number in grams.';
      }
      return 'Joint weight needs to be a positive weight in grams.';
    }
    return null;
  }

  const parsed = parseStrictPositiveDecimal(input.targetText);
  if (!parsed.ok) {
    if (parsed.reason === 'empty') {
      return 'Enter a target THC amount in milligrams.';
    }
    if (parsed.reason === 'malformed') {
      return 'Target THC must be an ordinary decimal number in milligrams.';
    }
    return 'Target THC should be a positive number in milligrams.';
  }
  return null;
}

/** True when the single-strain Calculate button should be enabled. */
export function isSingleStrainCalculateEnabled(
  input: SingleStrainValidationInput,
): boolean {
  return getSingleStrainValidationHint(input) === null;
}
